import type {AutocompleteRenderGroupParams} from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';

import {headingKey} from './selectOption';
import type {SelectOption} from './selectOption';

export interface HeadingCheckbox {
    checked: boolean;
    indeterminate: boolean;
    disabled: boolean;
    onToggle: () => void;
}

// Indent bulk-selection levels by one checkbox width plus its gap; other lists stay flush.
const INDENT = 3;

interface SelectHeadingProps {
    label: string;
    kind: 'section' | 'group';
    /** Extra left padding in theme spacing units. */
    indent: number;
    sticky: boolean;
    checkbox?: HeadingCheckbox;
}

const SelectHeading = ({label, kind, indent, sticky, checkbox}: SelectHeadingProps) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            minHeight: 36,
            pl: 2 + indent,
            pr: 2,
            bgcolor: 'background.paper',
            ...(sticky ? {position: 'sticky', top: -8, zIndex: 1} : {})
        }}
    >
        {!!checkbox && (
            <Checkbox
                checked={checkbox.checked}
                indeterminate={checkbox.indeterminate}
                disabled={checkbox.disabled}
                onChange={checkbox.onToggle}
                size={'small'}
                tabIndex={-1}
                inputProps={{'aria-label': label}}
                // Align with row checkboxes while preserving the hover/ripple area.
                sx={{p: 0.5, m: -0.5}}
            />
        )}

        <Typography
            variant={'body2'}
            fontWeight={kind === 'section' ? 600 : 500}
            color={kind === 'section' ? 'text.primary' : 'text.secondary'}
        >
            {label}
        </Typography>
    </Box>
);

interface SelectGroupProps {
    params: AutocompleteRenderGroupParams;
    visibleOptions: SelectOption[];
    getHeadingCheckbox?: (options: SelectOption[]) => HeadingCheckbox;
}

const SelectGroup = ({params, visibleOptions, getHeadingCheckbox}: SelectGroupProps) => {
    const members = visibleOptions.filter(option => headingKey(option) === params.group);
    const section = members[0]?.section;
    const group = members[0]?.group;
    const sectionMembers = section ? visibleOptions.filter(option => option.section === section) : [];
    const opensSection = !!section && headingKey(sectionMembers[0]) === params.group;
    const step = getHeadingCheckbox ? INDENT : 0;
    const depth = (section ? 1 : 0) + (group ? 1 : 0);

    return (
        <li>
            {section && opensSection && (
                <SelectHeading label={section} kind={'section'} indent={0} sticky checkbox={getHeadingCheckbox?.(sectionMembers)}/>
            )}

            {!!group && (
                <SelectHeading
                    label={group}
                    kind={'group'}
                    indent={section ? step : 0}
                    sticky={!section}
                    checkbox={getHeadingCheckbox?.(members)}
                />
            )}

            <Box component={'ul'} sx={{p: 0, m: 0, '& .MuiAutocomplete-option': {pl: 2 + depth * step}}}>
                {params.children}
            </Box>
        </li>
    );
};

export default SelectGroup;
